import { stringify } from 'query-string';

import { noteSessionRefusal } from '../auth/signOutReason';

export const GET_LIST = 'GET_LIST';
export const GET_ONE = 'GET_ONE';
export const GET_MANY = 'GET_MANY';
export const GET_MANY_REFERENCE = 'GET_MANY_REFERENCE';
export const CREATE = 'CREATE';
export const UPDATE = 'UPDATE';
export const UPDATE_MANY = 'UPDATE_MANY';
export const UPDATE_BATCH = 'UPDATE_BATCH';
export const DELETE = 'DELETE';
export const DELETE_MANY = 'DELETE_MANY';
export const DELETE_BATCH = 'DELETE_BATCH';
export const UPLOAD = 'UPLOAD';
export const BULK_ACTION = 'BULK_ACTION';

const appendFormDataFields = (formData, data) => {
    Object.keys(data).forEach((k) => {
        const v = data[k];
        if (v === null || v === undefined) {
            return;
        }
        if (typeof v === 'object' && !(v instanceof File) && !(v instanceof Blob)) {
            return;
        }
        formData.append(k, v);
    });
};

export const RESTDataProvider = (apiUrl, httpClient, providerOptions) => {
    /**
     * @param {String} type React-admin request type, e.g. 'GET_LIST'
     * @param {String} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params Request parameters. Depends on the request type
     * @returns {Object} { url, options } The HTTP request parameters
     */
    const convertDataRequestToHttp = (type, resource, params) => {
        let url = '';
        let options = { signal: params.signal };
        switch (type) {
            case CREATE:
                url = `${apiUrl}/${resource}/`;
                if (params.urlSuffix) {
                    url = `${url}${params.urlSuffix}`;
                }
                options.method = 'POST';
                if (params.upload || params.files) {
                    const data = new FormData();
                    if (params.upload) {
                        appendFormDataFields(data, params.data);
                        if (params.data?.attachments?.length > 0) {
                            for (let index = 0; index < params.data?.attachments.length; index++) {
                                data.append('attachment', params.data?.attachments[index]);
                            }
                        }
                    }
                    if (params.files) {
                        Object.entries(params.files).forEach(([k, f]) => data.append(k, f));
                    }
                    data.append('_json', JSON.stringify(params.data));
                    options.body = data;
                    options.upload = true; // use multipart/form-data content type
                } else {
                    options.body = JSON.stringify(params.data);
                }
                break;
            case BULK_ACTION:
                url = `${apiUrl}/${resource}/batch/`;
                options.method = 'POST';
                options.body = JSON.stringify({
                    action: params.action,
                    data: params.data,
                });
                break;
            case GET_ONE:
                url = params.id ? `${apiUrl}/${resource}/${params.id}/` : `${apiUrl}/${resource}/`;
                if (params.urlSuffix) {
                    url = `${url}${params.urlSuffix}`;
                    delete params.urlSuffix;
                }
                const { filter } = params && params.filter ? params : {};
                const query = {
                    ...filter,
                };
                url += filter ? `?${stringify(query)}` : '';

                // allow extra parameters to be appended
                const { id: _id, filter: _filter, keepSessionOn403: _keepSession, ...remainingParams } = params;
                if (remainingParams && Object.keys(remainingParams).length > 0) {
                    if (url.includes('?')) {
                        url = `${url}&${stringify(remainingParams)}`;
                    } else {
                        url = `${url}?${stringify(remainingParams)}`;
                    }
                }

                break;
            case GET_LIST: {
                const { page, perPage } = params && params.pagination ? params.pagination : { page: 1, perPage: 25 };
                const { field, order } = params && params.sort ? params.sort : { field: 'id', order: 'ASC' };
                const { filter } = params && params.filter ? params : {};
                const query = {
                    page,
                    page_size: perPage,
                    ordering: `${order.toUpperCase() === 'ASC' ? '' : '-'}${field}`,
                };
                if (filter && typeof filter === 'object') {
                    for (const [k, v] of Object.entries(filter)) {
                        if (v != null && v !== '') {
                            query[k] = v;
                        }
                    }
                }
                url = `${apiUrl}/${resource}/`;
                if (params?.urlSuffix) {
                    url = `${url}${params.urlSuffix}`;
                }
                if (url.includes('?')) {
                    url = `${url}&${stringify(query)}`;
                } else {
                    url = `${url}?${stringify(query)}`;
                }

                const {
                    pagination: _page,
                    sort: _sort,
                    filter: _filter,
                    convert: _convert,
                    signal: _signal,
                    ...remainingParams
                } = params;
                if (remainingParams && Object.keys(remainingParams).length > 0) {
                    if (url.includes('?')) {
                        url = `${url}&${stringify(remainingParams)}`;
                    } else {
                        url = `${url}?${stringify(remainingParams)}`;
                    }
                }

                break;
            }
            case GET_MANY_REFERENCE: {
                const { page, perPage } = params.pagination;
                const { field, order } = params.sort;
                const { filter, target, id } = params;
                const query = {
                    page,
                    page_size: perPage,
                    ordering: `${order.toUpperCase() === 'ASC' ? '' : '-'}${field}`,
                    ...filter,
                    [target]: id,
                };
                url = `${apiUrl}/${resource}/?${stringify(query)}`;
                break;
            }
            case UPDATE:
                url = `${apiUrl}/${resource}/${params.id}/`;
                if (params.urlSuffix) {
                    url = `${url}${params.urlSuffix}`;
                }
                options.method = 'PUT';
                if (params.upload || params.files) {
                    const data = new FormData();
                    if (params.upload) {
                        appendFormDataFields(data, params.data);
                        if (params.data?.attachments?.length > 0) {
                            for (let index = 0; index < params.data?.attachments.length; index++) {
                                data.append('attachment', params.data?.attachments[index]);
                            }
                        }
                    }
                    if (params.files) {
                        Object.entries(params.files).forEach(([k, f]) => data.append(k, f));
                    }
                    data.append('_json', JSON.stringify(params.data));
                    options.body = data;
                    options.upload = true; // use multipart/form-data content type
                } else {
                    options.body = JSON.stringify(params.data);
                }
                break;
            case UPDATE_BATCH:
                url = `${apiUrl}/${resource}/`;
                if (params.urlSuffix) {
                    url = `${url}${params.urlSuffix}`;
                }
                options.method = 'PUT';
                options.body = JSON.stringify(params.data);
                break;
            case DELETE:
                // `singleton: true` addresses the resource itself (DELETE /mfa/), never a record without an id.
                url = params.singleton ? `${apiUrl}/${resource}/` : `${apiUrl}/${resource}/${params.id}/`;
                options.method = 'DELETE';
                options.body = JSON.stringify(params.data);
                break;
            case DELETE_BATCH:
                url = `${apiUrl}/${resource}/batch/`;
                options.method = 'DELETE';
                options.body = JSON.stringify({
                    action: 'delete',
                    data: params.data,
                });
                break;
            case UPLOAD:
                console.log('upload: dataProvider: params:', params);
                url = `${apiUrl}/${resource}/`;
                if (params.urlSuffix) {
                    url = `${url}${params.urlSuffix}`;
                }
                options.method = 'PUT';
                const data = new FormData();
                appendFormDataFields(data, params.data || {});
                data.append('_json', JSON.stringify(params.data));
                options.body = data;
                options.upload = true; // use multipart/form-data content type
                console.log('upload: dataProvider: options:', options);
                break;
            default:
                throw new Error(`Unsupported Data Provider request type ${type}`);
        }

        return { url, options };
    };

    /**
     * @param {Object} response wrapper around the HTTP response from fetch(). The raw HTTP response is available in response.res
     * @param {String} type React-admin request type, e.g. 'GET_LIST'
     * @param {String} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params Request parameters. Depends on the request type
     * @returns {Object} Data response
     */
    const convertHttpResponse = (response, type, resource, params) => {
        const { headers } = response;

        // 401 (no valid token) and 403 sign the session out: the backend's dashboard gate
        // (IsDashboardUser) answers 403 to deleted, inactive and non-staff users. A request
        // that sets keepSessionOn403 gets its 403 back like any other response instead --
        // for background fetches the user never asked for (a menu badge count), where a 403
        // only means "not for you" and must not end the session.
        const keepSession = response.status === 403 && params && params.keepSessionOn403;
        if ((response.status === 401 || response.status === 403) && !keepSession && providerOptions && providerOptions.onAuthRequest) {
            // A password session the backend no longer accepts (two-step sign-in now required, or SSO only):
            // the sign-in page says so after the sign-out.
            if (response.status === 401) {
                noteSessionRefusal(response.json);
            }
            return providerOptions.onAuthRequest(response, type, resource, params, providerOptions);
        }

        const { json } = params.convert ? { json: params.convert(response.json, headers, type, resource, params) } : response;
        console.debug('convertHttpResponse', response, type, resource, params);
        if (!json) {
            return { data: [], total: 0, statusCode: response.status, headers: response.headers, response: response.res };
        }
        switch (type) {
            case GET_LIST:
            case GET_MANY_REFERENCE:
                if ('count' in json) {
                    const result = {
                        data: json.results,
                        total: json.count,
                        statusCode: response.status,
                        headers: response.headers,
                        next: json.next,
                        response: response.res,
                        tour_name: json.tour_name,
                        has_staff_chat_groups: json.has_staff_chat_groups,
                    };
                    if ('async_count_handle' in json) {
                        result.async_count_handle = json.async_count_handle;
                    }
                    // Present only when the conversations list actually applied the
                    // recent-activity recency window; lets the empty state say how far
                    // back it looked instead of implying there is nothing at all.
                    if ('recency_window_hours' in json) {
                        result.recency_window_hours = json.recency_window_hours;
                    }
                    return result;
                } else if (headers.has('content-range')) {
                    return {
                        data: json,
                        total: parseInt(headers.get('content-range').split('/').pop(), 10),
                        response: response.res,
                    };
                } else if ('detail' in json && json.detail === 'Invalid page.') {
                    return { data: [], total: 0, statusCode: response.status, headers: response.headers, response: response.res, };
                } else {
                    //The activity/id/used_by endpoint return an array instead of an object upon successful request
                    if (json.length === 0 || json.length > 0) {
                        return json;
                    }
                    throw new Error(
                        'The total number of results is unknown. The DRF data provider ' +
                        'expects responses for lists of resources to contain this ' +
                        "information to build the pagination. If you're not using the " +
                        'default PageNumberPagination class, please include this ' +
                        'information using the Content-Range header OR a "count" key ' +
                        'inside the response.'
                    );
                }
            case CREATE:
                return { data: json, statusCode: response.status, headers: response.headers, response: response.res };
            case DELETE:
                if (response.status < 400) {
                    return { data: params.previousData, statusCode: response.status, headers: response.headers, response: response.res };
                } else {
                    return { data: json, statusCode: response.status, headers: response.headers, response: response.res };
                }
            default:
                return { data: json, statusCode: response.status, headers: response.headers, response: response.res };
        }
    };

    /**
     * @param {String} type React-admin request type, e.g. 'GET_LIST'
     * @param {string} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params Request parameters. Depends on the request type
     * @returns {Promise} the Promise for a data response
     */
    return (type, resource, params) => {
        /**
         * Split GET_MANY, UPDATE_MANY and DELETE_MANY requests into multiple promises,
         * since they're not supported by default.
         */
        switch (type) {
            case GET_MANY:
                return Promise.all(
                    params.ids.map((id) =>
                        httpClient(`${apiUrl}/${resource}/${id}/`, {
                            method: 'GET',
                        })
                    )
                ).then((responses) => ({
                    data: responses.map((response) => response.json),
                }));
            case UPDATE_MANY:
                return Promise.all(
                    params.ids.map((id) =>
                        httpClient(`${apiUrl}/${resource}/${id}/`, {
                            method: 'PUT',
                            body: JSON.stringify(params.data),
                        })
                    )
                ).then((responses) => ({
                    data: responses.map((response) => response.json),
                }));
            case DELETE_MANY:
                return Promise.all(
                    params.ids.map((id) =>
                        httpClient(`${apiUrl}/${resource}/${id}/`, {
                            method: 'DELETE',
                        })
                    )
                ).then((responses) => ({
                    responses: responses.map((response) => ({ statusCode: response.status, data: response.json })),
                }));
            default:
                break;
        }

        const { url, options } = convertDataRequestToHttp(type, resource, params);
        return httpClient(url, options).then((response) => convertHttpResponse(response, type, resource, params));
    };
};

export default RESTDataProvider;
